import React, { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Mail, CheckCircle, XCircle, Clock, AlertCircle } from 'lucide-react';
import { useEmailLogs } from '../../../hooks/useEmailAdmin';

export default function EmailLogs() {
  const [filters, setFilters] = useState({
    status: '',
    emailType: '',
    startDate: '',
    endDate: '',
    page: '1',
    limit: '50',
  });

  const { logs, total, loading, error } = useEmailLogs(filters);

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value, page: '1' }));
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'SENT': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'DELIVERED': return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'FAILED': return <XCircle className="h-4 w-4 text-red-500" />;
      case 'BOUNCED': return <AlertCircle className="h-4 w-4 text-orange-500" />;
      default: return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'SENT': return 'text-green-600 dark:text-green-400';
      case 'DELIVERED': return 'text-green-700 dark:text-green-300';
      case 'FAILED': return 'text-red-600 dark:text-red-400';
      case 'BOUNCED': return 'text-orange-600 dark:text-orange-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  if (error) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 rounded-md">
        Error: {error}
      </div>
    );
  }

  const currentPage = parseInt(filters.page);
  const totalPages = Math.ceil(total / parseInt(filters.limit));

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold flex items-center gap-2">
        <Mail className="h-5 w-5" />
        Email Logs
      </h2>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
        <div>
          <label className="block text-sm font-medium mb-1">Status</label>
          <select 
            value={filters.status} 
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="w-full p-2 border rounded-md dark:bg-gray-700 dark:border-gray-600"
          >
            <option value="">All</option>
            <option value="SENT">Sent</option>
            <option value="DELIVERED">Delivered</option>
            <option value="FAILED">Failed</option>
            <option value="BOUNCED">Bounced</option>
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-1">Email Type</label>
          <select 
            value={filters.emailType} 
            onChange={(e) => handleFilterChange('emailType', e.target.value)}
            className="w-full p-2 border rounded-md dark:bg-gray-700 dark:border-gray-600"
          >
            <option value="">All</option>
            <option value="daily_digest">Daily Digest</option>
            <option value="weekly_digest">Weekly Digest</option>
            <option value="instant_alert">Instant Alert</option>
            <option value="verification">Verification</option>
            {/* Trial emails removed (service is fully free) */}
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-1">Start Date</label>
          <input
            type="date"
            value={filters.startDate}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
            className="w-full p-2 border rounded-md dark:bg-gray-700 dark:border-gray-600"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-1">End Date</label>
          <input
            type="date"
            value={filters.endDate}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
            className="w-full p-2 border rounded-md dark:bg-gray-700 dark:border-gray-600"
          />
        </div>
      </div>

      {/* Logs Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Recipient
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Email Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Subject
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Details
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Sent At
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {loading ? (
              <tr>
                <td colSpan={6} className="px-6 py-4 text-center">
                  Loading...
                </td>
              </tr>
            ) : logs.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                  No email logs found
                </td>
              </tr>
            ) : (
              logs.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(log.status)}
                      <span className={`text-sm font-medium ${getStatusColor(log.status)}`}>
                        {log.status}
                      </span>
                    </div>
                    {log.errorMessage && (
                      <div className="text-xs text-red-600 dark:text-red-400 mt-1">
                        {log.errorMessage}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">
                      <div className="font-medium text-gray-900 dark:text-white">
                        {log.recipientEmail}
                      </div>
                      {log.subscriber && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          ID: {log.subscriberId}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">
                      <div className="font-medium">{log.emailType}</div>
                      {log.template && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {log.template.language.toUpperCase()}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 dark:text-white max-w-xs truncate">
                      {log.subject}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">
                      {log.riskScore && (
                        <div>Risk: <span className="font-medium">{log.riskScore}/10</span></div>
                      )}
                      {log.articleCount !== null && log.articleCount !== undefined && (
                        <div>Articles: <span className="font-medium">{log.articleCount}</span></div>
                      )}
                      {log.categoriesIncluded && log.categoriesIncluded.length > 0 && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {log.categoriesIncluded.join(', ')}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">
                      {log.sentAt && (
                        <div className="text-gray-900 dark:text-white">
                          {formatDistanceToNow(new Date(log.sentAt))} ago
                        </div>
                      )}
                      {log.openedAt && (
                        <div className="text-xs text-green-600 dark:text-green-400">
                          Opened {formatDistanceToNow(new Date(log.openedAt))} ago
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <button
            onClick={() => handleFilterChange('page', String(currentPage - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="px-3 py-1">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => handleFilterChange('page', String(currentPage + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
